import { findSkillGroup, type SkillBranch } from '@sorrem/shared';
import { ESSENCE_COLORS } from '@sorrem-client/game/essence-integration';
import type { SkillTreeEffectColor } from './skill-tree-effects-renderer';
import { clamp01 } from './skill-tree-math';

/**
 * The skill tree's shared colors: the metals and mists its links and
 * frames are drawn in, the darks behind its emblems, and the light its
 * effects layer adds. Colors one piece alone uses stay with that piece.
 */

/** Martial's beveled gold, darkest to brightest (links and grouping-root frames). */
export const GOLD_SHADOW = '#2A1E0A';
export const GOLD_FACE = '#B48A3A';
export const GOLD_RIDGE = '#EBC774';
export const GOLD_HIGHLIGHT = '#FFF1C4';

/** Crafting's iron: the dark it is outlined and backed with, its face, and its glint. */
export const IRON_DARK = '#15141C';
export const IRON = '#7E848E';
export const IRON_HIGHLIGHT = '#C9CED6';

/** Essence's mist strands and glow. */
export const VIOLET = '#B49AF0';
export const VIOLET_PALE = '#E6DCFF';

export const WHITE = '#FFFFFF';

/** The dark inside Martial and Essence grouping-root frames. */
export const FRAME_DARK = '#14101F';

/** The tint for a group or skill with no color of its own. */
export const DEFAULT_TINT = '#E6E1F5';

/** Martial's emblem gold and Crafting's emblem iron; an Essence group takes its essence type's color. */
const MARTIAL_EMBLEM_TINT = '#E9C46A';
const CRAFTING_EMBLEM_TINT = '#AEB4BE';

/**
 * A grouping root's tint, which its skills' emblems share: Martial gold,
 * Crafting iron, and each Essence group its essence type's color
 * (`ESSENCE_COLORS`). An unknown group gets `DEFAULT_TINT`.
 */
export function resolveSkillGroupTint(groupId: string): string {
    const group = findSkillGroup(groupId);
    if (group === undefined) {
        return DEFAULT_TINT;
    }
    switch (group.branch) {
        case 'martial':
            return MARTIAL_EMBLEM_TINT;
        case 'essence':
            return group.essenceType === undefined ? DEFAULT_TINT : ESSENCE_COLORS[group.essenceType];
        case 'crafting':
            return CRAFTING_EMBLEM_TINT;
        default: {
            const unknownBranch: never = group.branch;
            return unknownBranch;
        }
    }
}

/** The dark behind each branch's skill cores, which an emblem's knockouts are filled with. */
export const SKILL_TREE_CORE_FILL: Readonly<Record<SkillBranch, string>> = { martial: '#1B1730', essence: '#251D3C', crafting: IRON_DARK };

/** The shadow under every label on the tree. */
export const TEXT_SHADOW = '#05040C';

/** The effects layer's light: each branch's base color, and the hot core it burns to. */
export const LIGHT_GOLD = '#F2C766';
export const LIGHT_GOLD_HOT = '#FFF2CC';
export const LIGHT_VIOLET = '#A885F2';
export const LIGHT_VIOLET_HOT = '#F2E6FF';
export const LIGHT_IRON = '#D9E0ED';

/** An RGB color, 0 to 255 per channel. */
export type SkillTreeRgb = readonly [number, number, number];

/** `#RRGGBB` as 0 to 255 channels. */
export function hexToRgb(hex: string): SkillTreeRgb {
    const channel = (offset: number): number => parseInt(hex.slice(offset, offset + 2), 16);
    return [channel(1), channel(3), channel(5)];
}

/** `#RRGGBB` as the effects layer's 0 to 1 channels. */
export function hexToEffectColor(hex: string): SkillTreeEffectColor {
    const [r, g, b] = hexToRgb(hex);
    return [r / 255, g / 255, b / 255];
}

/** An effect color as 0 to 255 channels, clamped and rounded. */
export function effectColorToRgb(color: SkillTreeEffectColor): SkillTreeRgb {
    const channel = (value: number): number => Math.round(clamp01(value) * 255);
    return [channel(color[0]), channel(color[1]), channel(color[2])];
}
